import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  viewChild,
  inject,
} from '@angular/core';
import {
  FileV2Info,
  ModalRef,
  BackendResponse,
  ModalV2Component,
  MachineInfo,
  ToolingInfo,
  ToastV2Service,
  UserRightsService,
  ModalModule,
  ButtonV2Module,
  DateV2Module,
} from '@schenck/rotec-components';
import { Observable } from 'rxjs';
import { TaskBase } from 'src/api';
import {
  TaskModalMainHeaderItem,
  TaskModalMainComponent,
} from '../task-modal-main/task-modal-main.component';
import { TaskModalService } from '../task-modal.service';
import { TaskInfoDetailItem, TaskInfoComponent } from '../task-info/task-info.component';
import { TaskStatusComponent } from '../task-status/task-status.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { TaskDocumentsComponent } from '../task-documents/task-documents.component';

export enum HistoryModalAction {
  Undo = 'Undo',
}

@Component({
  selector: 'ma-history-modal',
  templateUrl: './history-modal.component.html',
  styleUrls: ['./history-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    TaskModalMainComponent,
    TaskStatusComponent,
    TaskInfoComponent,
    TranslatePipe,
    TranslateDirective,
    TaskDocumentsComponent,
    ButtonV2Module,
    AsyncPipe,
    DateV2Module,
  ],
})
export class HistoryModalComponent<Task extends TaskBase> implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalRef = inject<ModalRef<string, HistoryModalAction>>(ModalRef);
  private readonly toastService = inject(ToastV2Service);
  public taskModalService = inject<TaskModalService<Task>>(TaskModalService);
  public userRightsService = inject(UserRightsService);

  /**
   * The task
   */
  @Input() public task: Task;
  /**
   * Additional task information that should be shown in the header.
   */
  @Input() public taskInfos: TaskModalMainHeaderItem[][] = [];
  /**
   * Additional defatils that should be shown inside the expandable.
   */
  @Input() public details: TaskInfoDetailItem[][];
  /**
   * Picture id of the asset associated to the task.
   */
  @Input() public assetPictureId: string;
  /**
   * The assigned asset
   */
  @Input() public asset: MachineInfo | ToolingInfo;

  public readonly modal = viewChild<ModalV2Component>('modal');
  public loading = false;
  public modalTitle = '';
  public isSeries = false;
  public taskDocuments$: Observable<FileV2Info[]>;

  public get isUndoBtnDisabled(): boolean {
    return (
      this.taskModalService.taskLoading$.value ||
      this.asset?.isDeactivated ||
      !this.task.serialTask?.isActive ||
      this.loading
    );
  }

  constructor() {
    this.taskModalService.setId(this.modalRef.data);
  }

  public ngOnChanges(): void {
    if (!this.task) {
      return;
    }

    this.modalTitle =
      this.task.schenckId && this.task.customerId
        ? this.task.schenckId + ' / ' + this.task.customerId
        : this.task.schenckId || this.task.customerId || this.task.title;
    this.isSeries = !!this.task.scheduling;
  }

  public close(): void {
    this.modalRef.close();
  }

  public undo(): void {
    this.loading = true;
    this.taskModalService.undo$().subscribe(
      BackendResponse.reactToResponse<void>({
        success: () => this.modalRef.emitAndClose(HistoryModalAction.Undo),
        badRequest: (msg) => {
          this.showErrorMsg(msg);
        },
        notFound: () => {
          this.showErrorMsg('VALIDATION_ERROR_Series does not exist anymore');
        },
        errorDefault: () => {
          this.showErrorMsg('TECHNICAL_ERROR_try again later or contact admin');
        },
      })
    );
  }

  private showErrorMsg(msg: string): void {
    this.loading = false;
    this.toastService.openError(msg);
    this.cdr.detectChanges();
  }

  public setDocuments(ids: string[]): void {
    const hasAddedDocs = this.task.taskDocumentIds.length < ids.length;
    this.taskModalService.setTaskDocuments$(ids).subscribe(() => {
      if (hasAddedDocs) {
        this.scrollBottom();
      }
    });
  }

  private scrollBottom(): void {
    setTimeout(() => this.modal().setScrollTop(10000), 1000);
  }
}
